/**
 * Signing in with an account: Google, through Supabase Auth.
 *
 * There is no email or password login. Signed out, the app still shares and
 * watches windows; friends need an account. Once signed in, the access token
 * goes to the friends connection (`friends.signIn`), which the server checks
 * before it lets the account do anything.
 *
 * Which Supabase project to use comes from the build: `VITE_SUPABASE_URL` and
 * `VITE_SUPABASE_PUBLISHABLE_KEY` (see `apps/web/.env.production`). In
 * `vite dev` they default to the local `supabase start` stack, whose keys are
 * public demo values. With neither set, accounts are simply off.
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js'
import { friends } from './friends.svelte'
import { isMobileBuild, isDesktopApp } from './platform'
import { desktopAuthCallback } from './auth-callback'

// The publishable key printed by `supabase start`. It is the same on every
// machine and only grants what row-level security allows, so it is safe to commit.
const LOCAL_URL = 'http://127.0.0.1:55321'
const LOCAL_KEY = 'sb_publishable_ACJWlzQHlZjBrEguHvfOxg_3BJgxAaH'

// Discord sign-in is temporarily disabled. Existing sessions still restore.
export type Provider = 'google'

function config(): { url: string; key: string } | null {
  // Helper webviews share the saved login with the main window. Restoring it
  // here would open another social connection and displace the streamer's.
  if (isDesktopApp && /^\/(mini|overlay)\/*$/.test(location.pathname)) return null
  // Mobile stays guest-only until external-browser OAuth callbacks are implemented.
  if (isMobileBuild) return null
  const url = import.meta.env.VITE_SUPABASE_URL || (import.meta.env.DEV ? LOCAL_URL : '')
  const key =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || (import.meta.env.DEV ? LOCAL_KEY : '')
  return url && key ? { url, key } : null
}

class Account {
  /** Whether a Supabase project is configured at all. */
  readonly available: boolean
  session = $state<Session | null>(null)
  /** Saved credentials are still being checked; this is not signed out. */
  restoring = $state(true)
  get signedIn(): boolean {
    return this.session !== null && !this.session.user.is_anonymous
  }
  busy = $state(false)
  /** The last problem, in words a person can act on. */
  error = $state<string | null>(null)

  #client: SupabaseClient | null = null

  constructor() {
    const settings = config()
    this.available = settings !== null
    if (!settings) {
      this.restoring = false
      return
    }
    this.#client = createClient(settings.url, settings.key, {
      auth: { flowType: 'pkce', detectSessionInUrl: !isDesktopApp, persistSession: true },
    })
    void this.#client.auth
      .getSession()
      .then(({ data, error }) => {
        if (error) this.error = error.message
        this.#adopt(data.session)
        // `?devuser=alice` signs in as a test user at once (dev builds only).
        const who = import.meta.env.DEV ? new URLSearchParams(location.search).get('devuser') : null
        if (who && data.session?.user.email !== `dev-${who.toLowerCase()}@sofa.test`)
          void this.signInAsTestUser(who)
      })
      .catch((err) => {
        this.error = err instanceof Error ? err.message : String(err)
      })
      .finally(() => {
        this.restoring = false
      })
    this.#client.auth.onAuthStateChange((_event, session) => this.#adopt(session))
    window.addEventListener('sofa-account-deleted', () => {
      void this.#client?.auth.signOut({ scope: 'local' })
      this.#adopt(null)
    })
    if (isDesktopApp) void this.#listenForLogin()
  }

  async #listenForLogin(): Promise<void> {
    try {
      const { getCurrent, onOpenUrl } = await import('@tauri-apps/plugin-deep-link')
      await onOpenUrl((urls) => {
        for (const url of urls) void this.#finishLogin(url)
      })
      for (const url of (await getCurrent()) ?? []) await this.#finishLogin(url)
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err)
    }
  }

  #exchanging = false
  #consumed = new Set<string>()
  async #finishLogin(input: string): Promise<void> {
    const callback = desktopAuthCallback(input)
    if (!callback || !this.#client || this.#exchanging) return
    if (callback.error) {
      this.error = callback.error
      return
    }
    if (this.#consumed.has(callback.code)) return
    this.#exchanging = true
    this.busy = true
    try {
      const { error } = await this.#client.auth.exchangeCodeForSession(callback.code)
      if (error) throw error
      this.#consumed.add(callback.code)
      history.replaceState(null, '', '/friends')
      window.dispatchEvent(new PopStateEvent('popstate'))
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err)
    } finally {
      this.#exchanging = false
      this.busy = false
    }
  }

  /** The name the provider gave the account, for "Signed in as". */
  get name(): string | null {
    const meta = this.session?.user.user_metadata as
      { full_name?: string; name?: string; user_name?: string } | undefined
    return meta?.full_name || meta?.name || meta?.user_name || null
  }

  /** Which provider the account was made with, when Supabase says so. */
  get provider(): string | null {
    return (this.session?.user.app_metadata?.provider as string | undefined) ?? null
  }

  #adopt(session: Session | null): void {
    this.session = session
    // An anonymous Supabase session is not an account, so it gets no friends.
    if (session && !session.user.is_anonymous) friends.signIn(session.access_token)
    else friends.signOut()
    // The OAuth round trip lands here; tidy the URL once it has been consumed.
    if (session && location.pathname === '/auth/callback') {
      history.replaceState(null, '', '/friends')
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  /** Redirects to the provider and back. The session arrives through `#adopt`. */
  async signInWith(provider: Provider): Promise<void> {
    if (!this.#client || provider !== 'google') return
    this.busy = true
    this.error = null
    try {
      const { data, error } = await this.#client.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: isDesktopApp ? 'sofa://auth/callback' : `${location.origin}/auth/callback`,
          skipBrowserRedirect: isDesktopApp,
        },
      })
      if (error) this.error = error.message
      else if (isDesktopApp && data.url) {
        const { openUrl } = await import('@tauri-apps/plugin-opener')
        await openUrl(data.url)
      }
    } catch (caught) {
      this.error = caught instanceof Error ? caught.message : String(caught)
    } finally {
      this.busy = false
    }
  }

  /**
   * Development only: sign in as a throwaway test user on the local Supabase
   * stack, creating it the first time. `who` is any short name (letters and
   * digits), giving `dev-<who>@sofa.test`. Needs no Google or Discord app, and
   * is compiled out of release builds (`import.meta.env.DEV` is false there).
   * The signaling server must accept the email provider:
   * `SSHARE_AUTH_PROVIDERS=google,discord,email`.
   */
  async signInAsTestUser(who: string): Promise<void> {
    const slug = who
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .slice(0, 20)
    if (!import.meta.env.DEV || !slug || !this.#client || config()?.url !== LOCAL_URL) return
    this.busy = true
    this.error = null
    const credentials = { email: `dev-${slug}@sofa.test`, password: 'sofa-dev-password' }
    try {
      let { error } = await this.#client.auth.signInWithPassword(credentials)
      if (error) {
        // First use on this database: make the user, which also signs in.
        const created = await this.#client.auth.signUp({
          ...credentials,
          options: { data: { full_name: `Test ${slug}` } },
        })
        error = created.error
      }
      if (error) this.error = error.message
    } catch (caught) {
      this.error = caught instanceof Error ? caught.message : String(caught)
    } finally {
      this.busy = false
    }
  }

  async signOut(): Promise<void> {
    await this.#client?.auth.signOut()
  }
}

export const account = new Account()
